/**
 * stargazing_spots.ts — PURE finders for the debug menu's "Stargazing" teleports (2026-10-06): the nearest (or a random)
 * capital, city, town, player city or hamlet centre, and the nearest truly dark sky (Bortle 1 on dry land), each with the
 * sky reading THERE from the same light-pollution sources the sky draws (`lib/light_pollution.ts` over the live
 * `skyglowLive().sources`). A settlement spot is its centre, nudged onto open ground when a wall stands there (the
 * structure masks); the dark spot is a ring scan from the player, then a continental lattice when nothing within 90 km
 * qualifies. No three, no DOM — `verify:stargazing-spots` pins it.
 */
import { ringScan, coarseNearest, CONTINENT_HALF_M } from './terrain_features.js';
import { skyglowAt, TIER_GLOW, sourceRatio, saturateRatio, sqmFromRatio, nelmFromSqm, bortleFromSqm, type LightSource, type Skyglow } from './light_pollution.js';

export type StarSpotKind = 'capital' | 'city' | 'town' | 'player' | 'hamlet' | 'dark';
export type StarSpotMode = 'nearest' | 'random';

/** The darkest a sky gets (Bortle 1 is SQM ≥ 21.99 — `bortleFromSqm`). */
export const DARK_SQM = 21.99;
/** How far a settlement centre may be nudged to reach open ground (m). */
export const OPEN_SPOT_MAX_M = 120;
/** Clearance around a landing spot (m): the point and four points this far out must all be open ground. */
export const OPEN_SPOT_CLEAR_M = 1.5;
/**
 * A settlement SHOWS ITS OWN SKY when its own light is at least this share of the glow at its centre. The hamlet nearest
 * the hub stands 1.8 km from a city and wears the city's Bortle 8 — the buttons prefer the nearest settlement that
 * stands on its own, and fall back to the plain nearest only when none of that kind does.
 */
export const OWN_SKY_SHARE = 0.5;

/** The share of the glow at a source's own centre that is its own light (0..1; 0 for a source with no light). */
export function ownSkyShare(own: Pick<LightSource, 'id' | 'x' | 'z' | 'A' | 'r'>, sources: ReadonlyArray<LightSource>): number {
  const mine = sourceRatio(own, 0);
  if (!(mine > 0)) return 0;
  let total = 0;
  for (const s of sources) {
    const dKm = Math.hypot(s.x - own.x, s.z - own.z) / 1000;
    total += s.id === own.id ? mine : sourceRatio(s, dKm);
  }
  return total > 0 ? mine / total : 0;
}

/** A lone settlement's sky at its own centre (the reading a row's hint shows). */
export function loneCentreGlow(tier: 'capital' | 'city' | 'town' | 'player' | 'hamlet'): Skyglow {
  const g = TIER_GLOW[tier];
  const sqm = sqmFromRatio(saturateRatio(sourceRatio(g, 0)));
  return { ratio: saturateRatio(sourceRatio(g, 0)), sqm, bortle: bortleFromSqm(sqm), nelm: nelmFromSqm(sqm), tint: [1, 1, 1] };
}

export const STAR_SPOT_KINDS: ReadonlyArray<{ kind: StarSpotKind; label: string; hint: string }> = [
  { kind: 'capital', label: 'Capital centre', hint: 'the brightest sky in the world — a handful of stars' },
  { kind: 'city', label: 'City centre', hint: 'the brightest stars and planets only' },
  { kind: 'town', label: 'Town centre', hint: 'the main constellations' },
  { kind: 'player', label: 'Player city', hint: 'a player-founded city — sized by its rank and what it has built' },
  { kind: 'hamlet', label: 'Hamlet', hint: 'a few thousand stars, the Milky Way faintly' },
  { kind: 'dark', label: 'Dark wilds', hint: 'Bortle 1 on dry land — the full sky, the Milky Way with structure' },
];

/** A settlement as the finder needs it (`SettlementSite` fits). */
export interface StarSite { id: number; centerX: number; centerZ: number; tier: string }

export interface StarSpotData {
  /** The settlement sites (districts are part of their capital — never a target). */
  sites: ReadonlyArray<StarSite>;
  /** Every light source in the world: the settlements AND the player cities (`p<cityId>`). */
  sources: ReadonlyArray<LightSource>;
  /** Dry, walkable ground at (x, z)? (above the sea, not in a lake) */
  land: (x: number, z: number) => boolean;
  /** No wall at walking height at (x, z)? (the structure masks; true off every grid) */
  open: (x: number, z: number) => boolean;
  /** A settlement's display name (the site id). */
  siteName?: (site: StarSite) => string;
  /** A player city's display name (the source id `p<cityId>`). */
  playerName?: (sourceId: string) => string;
}

export interface StarSpot {
  kind: StarSpotKind;
  x: number; z: number;
  /** Human line ("Aurel Prime (capital)"). */
  label: string;
  distM: number;
  /** The sky over the spot, from every source (neighbours included). */
  glow: Skyglow;
  /** How far the landing was nudged off the centre to reach open ground (m). */
  nudgedM: number;
}

/** The nearest point within `maxR` where `ok` holds (rings of `step` metres outward), or null. */
export function clearSpotNear(x: number, z: number, ok: (x: number, z: number) => boolean, maxR = OPEN_SPOT_MAX_M, step = 2): { x: number; z: number } | null {
  if (ok(x, z)) return { x, z };
  for (let r = step; r <= maxR; r += step) {
    const n = Math.max(8, Math.ceil((2 * Math.PI * r) / step));
    const a0 = r * 0.61803; // rotate each ring so the samples never line up
    for (let i = 0; i < n; i++) {
      const a = a0 + (i / n) * Math.PI * 2;
      const px = x + Math.cos(a) * r, pz = z + Math.sin(a) * r;
      if (ok(px, pz)) return { x: px, z: pz };
    }
  }
  return null;
}

function fmtDist(d: number): string { return d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`; }

/** One line for the note: where, how far, and the sky there. */
export function starSpotLine(s: StarSpot): string {
  const g = s.glow;
  return `${s.label} · ${fmtDist(s.distM)} · Bortle ${g.bortle} · SQM ${g.sqm.toFixed(2)} · stars to mag ${g.nelm.toFixed(1)}`
    + (s.nudgedM > 0.5 ? ` · ${Math.round(s.nudgedM)} m off the centre (a building stands there)` : '');
}

/** One line for the sky over a point (the live "here" readout). */
export function skyLine(g: Skyglow): string {
  return `Bortle ${g.bortle} · SQM ${g.sqm.toFixed(2)} · stars to mag ${g.nelm.toFixed(1)}`;
}

function pick<T>(list: readonly T[], fromX: number, fromZ: number, at: (t: T) => { x: number; z: number }, mode: StarSpotMode, rand: () => number): T | null {
  if (list.length === 0) return null;
  if (mode === 'random') return list[Math.floor(rand() * list.length) % list.length];
  let best: T | null = null, bestD = Infinity;
  for (const t of list) {
    const p = at(t);
    const d = (p.x - fromX) * (p.x - fromX) + (p.z - fromZ) * (p.z - fromZ);
    if (d < bestD) { bestD = d; best = t; }
  }
  return best;
}

/** The spot of `kind` from (fromX, fromZ), or the reason there is none. */
export function findStarSpot(d: StarSpotData, kind: StarSpotKind, fromX: number, fromZ: number, mode: StarSpotMode = 'nearest', rand: () => number = Math.random): StarSpot | string {
  const finish = (x: number, z: number, label: string, nudgedM = 0): StarSpot =>
    ({ kind, x, z, label, distM: Math.hypot(x - fromX, z - fromZ), glow: skyglowAt(x, z, d.sources), nudgedM });
  // A landing spot: dry, and no wall within the clearance (a centre can stand in a plaza or under a landmark).
  const clear = (x: number, z: number): boolean => {
    const c = OPEN_SPOT_CLEAR_M;
    return d.open(x, z) && d.open(x + c, z) && d.open(x - c, z) && d.open(x, z + c) && d.open(x, z - c) && d.land(x, z);
  };
  const settle = (cx: number, cz: number, label: string): StarSpot | string => {
    const p = clearSpotNear(cx, cz, clear);
    if (!p) return `${label}: no open ground within ${OPEN_SPOT_MAX_M} m of its centre`;
    return finish(p.x, p.z, label, Math.hypot(p.x - cx, p.z - cz));
  };

  if (kind === 'player') {
    const all = d.sources.filter((s) => s.id.startsWith('p'));
    const alone = all.filter((s) => ownSkyShare(s, d.sources) >= OWN_SKY_SHARE);
    const players = alone.length ? alone : all;
    const s = pick(players, fromX, fromZ, (q) => ({ x: q.x, z: q.z }), mode, rand);
    if (!s) return 'no player city glows yet (an active or provisional city is a light source)';
    return settle(s.x, s.z, `${d.playerName?.(s.id) ?? `Player city ${s.id.slice(1)}`} (player city)`);
  }
  if (kind === 'dark') {
    const dark = (x: number, z: number): boolean => skyglowAt(x, z, d.sources).sqm >= DARK_SQM && d.land(x, z);
    let ox = fromX, oz = fromZ;
    if (mode === 'random') { ox = (rand() * 2 - 1) * CONTINENT_HALF_M; oz = (rand() * 2 - 1) * CONTINENT_HALF_M; }
    const near = ringScan(ox, oz, dark);
    const cell = near ?? coarseNearest(ox, oz, dark);
    if (!cell) return 'no Bortle 1 dry land anywhere (every land cell sees a town)';
    const p = near ?? ringScan(cell.x, cell.z, dark, 16_000) ?? cell;
    return finish(p.x, p.z, 'Dark wilds (Bortle 1)');
  }
  const all = d.sites.filter((s) => s.tier === kind);
  const byId = new Map(d.sources.map((q) => [q.id, q] as const));
  const alone = all.filter((s) => { const own = byId.get(String(s.id)); return own !== undefined && ownSkyShare(own, d.sources) >= OWN_SKY_SHARE; });
  const sites = alone.length ? alone : all;
  const s = pick(sites, fromX, fromZ, (q) => ({ x: q.centerX, z: q.centerZ }), mode, rand);
  if (!s) return `no ${kind} in this world`;
  return settle(s.centerX, s.centerZ, `${d.siteName?.(s) ?? `${kind[0].toUpperCase()}${kind.slice(1)} #${s.id}`} (${kind})`);
}
